import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Clock, MapPin, Users, CalendarDays } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { CategoryIcon } from "../components/CategoryIcon";
import { CATEGORY_META } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

const CACHE = "events_cache_v1";

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};

export default function Events() {
  const { t, catLabel } = useLang();
  const [events, setEvents] = useState(() => JSON.parse(localStorage.getItem(CACHE) || "null") || []);
  const [loading, setLoading] = useState(events.length === 0);
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  const load = () => api.get("/events").then((r) => {
    setEvents(r.data);
    localStorage.setItem(CACHE, JSON.stringify(r.data));
  }).finally(() => setLoading(false));

  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data)); }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div className="max-w-2xl">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">{t("events_title")}</h1>
          <p className="mt-2 text-stone-600 leading-relaxed">{t("events_sub")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="event-add-btn" className="rounded-full bg-[#C05E4B] hover:bg-[#A65140] gap-2 h-11 px-6 shrink-0">
              <CalendarPlus className="w-4 h-4" /> {t("events_add")}
            </Button>
          </DialogTrigger>
          <EventDialog categories={categories} onDone={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      {loading && events.length === 0 ? (
        <div className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={`es-${i}`} className="h-28 rounded-2xl" />)}</div>
      ) : events.length === 0 ? (
        <p className="text-center py-20 text-stone-400" data-testid="events-empty">{t("no_events")}</p>
      ) : (
        <div className="space-y-4" data-testid="events-list">
          {events.map((ev) => {
            const color = CATEGORY_META[ev.category]?.color || "#2E604A";
            const d = new Date(ev.date + "T00:00:00");
            return (
              <div key={ev.id} data-testid={`event-${ev.id}`}
                className="card-lift flex gap-4 bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
                <div className="flex flex-col items-center justify-center rounded-xl w-16 h-16 shrink-0"
                  style={{ backgroundColor: `${color}14`, color }}>
                  <span className="text-xs font-bold uppercase">{d.toLocaleDateString(undefined, { month: "short" })}</span>
                  <span className="text-2xl font-display font-extrabold leading-none">{d.getDate()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge className="rounded-full border-0 text-[11px] font-semibold"
                      style={{ backgroundColor: `${color}18`, color }}>
                      <CategoryIcon category={ev.category} className="w-3 h-3 mr-1" /> {catLabel(ev.category)}
                    </Badge>
                  </div>
                  <h3 className="font-display font-bold text-stone-900 text-lg leading-snug">{ev.title}</h3>
                  {ev.description && <p className="text-sm text-stone-600 mt-1 leading-relaxed">{ev.description}</p>}
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-stone-600">
                    <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-stone-400" />{fmtDate(ev.date)}</span>
                    {ev.time && <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-stone-400" />{ev.time}</span>}
                    {ev.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-stone-400" />{ev.location}</span>}
                    {ev.organization && <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-stone-400" />{ev.organization}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EventDialog({ categories, onDone }) {
  const { t, catLabel } = useLang();
  const [form, setForm] = useState({ title: "", description: "", date: "", time: "", location: "", organization: "", category: "community" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.title || !form.date) { toast.error("Please add a title and date."); return; }
    setBusy(true);
    try {
      await api.post("/events", form);
      toast.success(t("ev_ok"));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add event.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
      <DialogHeader><DialogTitle className="font-display text-start">{t("events_add")}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-sm font-semibold mb-1.5 block">{t("ev_title_label")}</Label>
          <Input data-testid="event-title" value={form.title} onChange={set("title")} className="rounded-xl h-11" />
        </div>
        <div>
          <Label className="text-sm font-semibold mb-1.5 block">{t("ev_desc_label")}</Label>
          <Textarea data-testid="event-desc" value={form.description} onChange={set("description")} rows={3} className="rounded-xl" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">{t("ev_date_label")}</Label>
            <Input data-testid="event-date" type="date" value={form.date} onChange={set("date")} className="rounded-xl h-11" />
          </div>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">{t("ev_time_label")}</Label>
            <Input data-testid="event-time" value={form.time} onChange={set("time")} className="rounded-xl h-11" />
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold mb-1.5 block">{t("ev_location_label")}</Label>
          <Input data-testid="event-location" value={form.location} onChange={set("location")} className="rounded-xl h-11" />
        </div>
        <div>
          <Label className="text-sm font-semibold mb-1.5 block">{t("ev_org_label")}</Label>
          <Input data-testid="event-org" value={form.organization} onChange={set("organization")} className="rounded-xl h-11" />
        </div>
        <div>
          <Label className="text-sm font-semibold mb-1.5 block">{t("ev_cat_label")}</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="event-category" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="event-submit" onClick={submit} disabled={busy}
          className="rounded-full bg-[#C05E4B] hover:bg-[#A65140] w-full h-11">
          {t("ev_submit")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
